<template>
  <div class="page-container">
    <!-- 头部 -->
    <div class="page-header">
      <div>
        <h2 class="page-title">官方内容</h2>
      </div>
      <div class="page-header-extra">
        <span>共 <b>{{ total }}</b> 篇</span>
      </div>
    </div>

    <!-- 搜索 + 操作 -->
    <el-card class="search-card" shadow="never">
      <div class="search-bar">
        <el-form :inline="true" :model="query" size="small" class="search-form">
          <el-form-item label="关键字">
            <el-input v-model="query.keyword" placeholder="标题/摘要关键词" clearable style="width: 200px" />
          </el-form-item>
          <el-form-item label="分类">
            <el-select v-model="query.category" placeholder="全部" clearable style="width: 140px">
              <el-option v-for="d in categories" :key="d._id" :label="d.label" :value="String(d._id)" />
            </el-select>
          </el-form-item>
          <el-form-item label="形式">
            <el-select v-model="query.type" placeholder="全部" clearable style="width: 120px">
              <el-option v-for="t in OFFICIAL_TYPE" :key="t.value" :label="t.label" :value="t.value" />
            </el-select>
          </el-form-item>
          <el-form-item label="状态">
            <el-select v-model="query.status" placeholder="全部" clearable style="width: 120px">
              <el-option v-for="s in OFFICIAL_STATUS" :key="s.value" :label="s.label" :value="s.value" />
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
            <el-button icon="el-icon-refresh" @click="resetSearch">重置</el-button>
          </el-form-item>
        </el-form>
        <div class="action-bar">
          <el-button type="primary" icon="el-icon-plus" size="small" @click="handleAdd">新增内容</el-button>
        </div>
      </div>
    </el-card>

    <!-- 表格 -->
    <el-card class="table-card" shadow="never">
      <el-table v-loading="loading" :data="list" border size="small" height="100%">
        <!-- 封面图 -->
        <el-table-column label="封面" width="100" align="center">
          <template slot-scope="scope">
            <div v-if="scope.row.cover" class="content-thumb">
              <el-image :src="scope.row.cover" :preview-src-list="[scope.row.cover]" fit="cover" style="width: 100%; height: 100%" />
            </div>
            <div v-else class="content-thumb content-thumb--empty">
              {{ scope.row.type === 2 ? '视频' : '无图' }}
            </div>
          </template>
        </el-table-column>
        <!-- 分类 -->
        <el-table-column label="分类" width="100" align="center">
          <template slot-scope="scope">{{ categoryLabel(scope.row.category) }}</template>
        </el-table-column>
        <!-- 标题 -->
        <el-table-column prop="title" label="标题" min-width="160" show-overflow-tooltip />
        <!-- 形式 -->
        <el-table-column label="形式" width="80" align="center">
          <template slot-scope="scope">
            <el-tag :type="scope.row.type === 2 ? 'danger' : 'primary'" size="mini" effect="plain">
              {{ officialTypeLabel(scope.row.type) }}
            </el-tag>
          </template>
        </el-table-column>
        <!-- 摘要 -->
        <el-table-column prop="summary" label="摘要" min-width="200" show-overflow-tooltip>
          <template slot-scope="scope">
            <span v-if="scope.row.summary">{{ scope.row.summary }}</span>
            <span v-else class="cell-muted">—</span>
          </template>
        </el-table-column>
        <!-- 点赞数 -->
        <el-table-column prop="like_count" label="点赞" width="90" align="center" />
        <!-- 发布时间 -->
        <el-table-column label="发布时间" min-width="170">
          <template slot-scope="scope">
            <span>{{ officialPublishText(scope.row) }}</span>
          </template>
        </el-table-column>
        <!-- 状态 -->
        <el-table-column label="状态" width="90" align="center">
          <template slot-scope="scope">
            <el-tag :type="officialStatusTagType(scope.row.status)" size="mini" effect="plain">
              {{ officialStatusText(scope.row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <!-- 操作 -->
        <el-table-column label="操作" width="230" align="center">
          <template slot-scope="scope">
            <el-button type="text" size="mini" @click="handleEdit(scope.row)">编辑</el-button>
            <el-button
              v-if="scope.row.status !== 1"
              type="text" size="mini"
              @click="handlePublish(scope.row)"
            >发布</el-button>
            <el-button
              v-else
              type="text" size="mini" class="text-warning-btn"
              @click="handleOffline(scope.row)"
            >下架</el-button>
            <el-button type="text" size="mini" class="text-danger-btn" @click="handleDelete(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
      <div class="pagination-wrap">
        <el-pagination
          background
          layout="total, prev, pager, next, sizes"
          :total="total"
          :current-page.sync="page"
          :page-size.sync="page_size"
          :page-sizes="[10, 20, 50]"
          @current-change="loadData"
          @size-change="handleSizeChange"
        />
      </div>
    </el-card>

    <!-- 编辑/新增弹框 -->
    <el-dialog
      :title="isEdit ? '编辑内容' : '新增内容'"
      :visible.sync="formVisible"
      width="780px"
      top="4vh"
      class="official-form-dialog"
      :close-on-click-modal="false"
      append-to-body
    >
      <el-form ref="form" :model="form" :rules="rules" label-width="100px" label-position="top">
        <div class="form-section">
          <div class="form-section-title">基础</div>
          <el-row :gutter="24">
            <el-col :span="12">
              <el-form-item label="分类" prop="category">
                <el-select v-model="form.category" placeholder="请选择" style="width: 100%">
                  <el-option v-for="d in categories" :key="d._id" :label="d.label" :value="String(d._id)" />
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="内容形式" prop="type">
                <el-radio-group v-model="form.type">
                  <el-radio :label="1">图文</el-radio>
                  <el-radio :label="2">视频</el-radio>
                </el-radio-group>
              </el-form-item>
            </el-col>
          </el-row>
          <el-form-item label="标题" prop="title">
            <el-input v-model="form.title" placeholder="如：仁风景从" maxlength="128" show-word-limit />
          </el-form-item>
          <el-form-item label="封面图" prop="cover">
            <image-uploader v-model="form.cover" />
            <span class="form-tip">图文缩略图 / 视频横版封面</span>
          </el-form-item>
        </div>

        <!-- 图文正文（type=1） -->
        <el-form-item v-if="form.type === 1" label="图文正文">
          <wechat-editor v-model="form.content" />
        </el-form-item>

        <!-- 视频（type=2）：上传到云端，小程序端直接播放 -->
        <el-form-item v-else label="视频" prop="video_url">
          <video-uploader v-model="form.video_url" dir="official/video" />
        </el-form-item>

        <div class="form-section">
          <div class="form-section-title">摘要</div>
          <el-form-item label="摘要" prop="summary">
            <el-input
              v-model="form.summary"
              type="textarea"
              :rows="3"
              maxlength="255"
              show-word-limit
              placeholder="列表页展示，超长截断"
            />
          </el-form-item>
        </div>

        <div class="form-section">
          <div class="form-section-title">发布控制</div>
          <el-row :gutter="24">
            <el-col :span="12">
              <el-form-item label="点赞数">
                <el-input-number v-model="form.like_count" :min="0" :max="9999999" controls-position="right" style="width: 100%" />
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="状态" prop="status">
                <el-radio-group v-model="form.status">
                  <el-radio :label="0">草稿</el-radio>
                  <el-radio :label="1">已发布</el-radio>
                  <el-radio :label="2">已下架</el-radio>
                </el-radio-group>
              </el-form-item>
            </el-col>
          </el-row>
          <el-form-item label="发布时间">
            <el-date-picker
              v-model="form.publish_time"
              type="datetime"
              placeholder="留空则取创建时间"
              value-format="yyyy-MM-dd HH:mm:ss"
              style="width: 100%"
            />
            <span class="form-tip">留空表示未发布时间，列表展示时取创建时间</span>
          </el-form-item>
        </div>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="formVisible = false">取 消</el-button>
        <el-button type="primary" :loading="submitting" @click="submit">保 存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  OFFICIAL_TYPE, OFFICIAL_STATUS,
  officialTypeLabel, officialStatusText, officialStatusTagType, officialPublishText
} from '@/mock/officialContent'
import {
  getOfficialList, createOfficial, updateOfficial, deleteOfficial, updateOfficialStatus
} from '@/api/official'
import dictOptions from '@/mixins/dict-options'
import ImageUploader from '@/components/ImageUploader.vue'
import VideoUploader from '@/components/VideoUploader.vue'
import WechatEditor from '@/components/WechatEditor.vue'

export default {
  name: 'OfficialContentManage',
  components: { ImageUploader, VideoUploader, WechatEditor },
  mixins: [dictOptions],
  data() {
    return {
      loading: false,
      list: [],
      total: 0,
      page: 1,
      page_size: 10,
      categories: [],
      query: { keyword: '', category: '', type: '', status: '' },
      formVisible: false,
      currentRow: null,
      submitting: false,
      form: this.emptyForm(),
      rules: {
        category: [{ required: true, message: '请选择分类', trigger: 'change' }],
        type: [{ required: true, message: '请选择内容形式', trigger: 'change' }],
        title: [{ required: true, message: '请输入标题', trigger: 'blur' }],
        status: [{ required: true, message: '请选择状态', trigger: 'change' }],
        video_url: [{
          validator: (rule, value, callback) => {
            if (this.form.type === 2 && !value) callback(new Error('请上传视频'))
            else callback()
          },
          trigger: 'change'
        }]
      },
      OFFICIAL_TYPE,
      OFFICIAL_STATUS
    }
  },
  computed: {
    isEdit() { return !!(this.currentRow && this.currentRow.id) }
  },
  created() {
    this.loadData()
    this.loadDictOptions({ content_category: 'categories' })
  },
  methods: {
    loadData() {
      this.loading = true
      const params = Object.assign({}, this.query, {
        page: this.page,
        page_size: this.page_size
      })
      getOfficialList(params).then(res => {
        if (res.code !== 200) return
        const data = res.data || {}
        this.list = data.list || []
        this.total = data.total || 0
      }).finally(() => {
        this.loading = false
      })
    },
    categoryLabel(value) {
      const item = this.categories.find(c => String(c._id) === String(value))
      return item ? item.label : (value || '—')
    },
    officialTypeLabel,
    officialStatusText,
    officialStatusTagType,
    officialPublishText,
    handleSearch() {
      this.page = 1
      this.loadData()
    },
    resetSearch() {
      this.query = { keyword: '', category: '', type: '', status: '' }
      this.page = 1
      this.loadData()
    },
    handleSizeChange(size) {
      this.page_size = size
      this.page = 1
      this.loadData()
    },
    handleAdd() {
      this.currentRow = null
      this.form = this.emptyForm()
      this.formVisible = true
      this.$nextTick(() => this.$refs.form && this.$refs.form.clearValidate())
    },
    handleEdit(row) {
      this.currentRow = Object.assign({}, row)
      this.form = Object.assign(this.emptyForm(), row, {
        publish_time: row.publish_time || ''
      })
      this.formVisible = true
      this.$nextTick(() => this.$refs.form && this.$refs.form.clearValidate())
    },
    handlePublish(row) {
      this.$confirm('确认发布该内容？发布后小程序端可见。', '发布确认', { type: 'warning' })
        .then(() => updateOfficialStatus(row.id, { status: 1 }))
        .then(res => {
          if (res.code !== 200) return
          this.$message.success('已发布')
          this.loadData()
        }).catch(() => {})
    },
    handleOffline(row) {
      this.$confirm('确认下架该内容？下架后小程序端不再展示。', '下架确认', { type: 'warning' })
        .then(() => updateOfficialStatus(row.id, { status: 2 }))
        .then(res => {
          if (res.code !== 200) return
          this.$message.success('已下架')
          this.loadData()
        }).catch(() => {})
    },
    handleDelete(row) {
      this.$confirm(`确认删除「${row.title || '该内容'}」？`, '删除确认', { type: 'warning' })
        .then(() => deleteOfficial(row.id))
        .then(res => {
          if (res.code !== 200) return
          this.$message.success('已删除')
          // 当前页删剩最后一条时回退一页
          if (this.list.length === 1 && this.page > 1) this.page -= 1
          this.loadData()
        }).catch(() => {})
    },
    submit() {
      this.$refs.form.validate(valid => {
        if (!valid) return
        // 白名单组装；正文/视频按形式互斥
        const payload = {
          category: this.form.category,
          type: this.form.type,
          title: this.form.title,
          cover: this.form.cover,
          summary: this.form.summary,
          content: this.form.type === 1 ? (this.form.content || '') : '',
          video_url: this.form.type === 2 ? (this.form.video_url || '') : '',
          like_count: this.form.like_count,
          publish_time: this.form.publish_time || '',
          status: this.form.status
        }
        this.submitting = true
        const req = this.isEdit
          ? updateOfficial(this.currentRow.id, payload)
          : createOfficial(payload)
        req.then(res => {
          if (res.code !== 200) return
          this.$message.success(this.isEdit ? '已保存' : '已新增')
          this.formVisible = false
          this.loadData()
        }).finally(() => {
          this.submitting = false
        })
      })
    },
    emptyForm() {
      return {
        id: 0,
        category: '',
        type: 1,
        title: '',
        cover: '',
        summary: '',
        content: '',
        video_url: '',
        like_count: 0,
        publish_time: '',
        status: 0
      }
    }
  }
}
</script>

<style scoped>
.content-thumb {
  width: 80px;
  height: 48px;
  border-radius: 4px;
  overflow: hidden;
  background: #f5f6f8;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
}
.content-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.content-thumb--empty {
  color: #c0c4cc;
  font-size: 12px;
  border: 1px dashed #dcdfe6;
  background: #fafafa;
}

/* 分段标题（与其他模块一致） */
.form-section {
  margin-bottom: 18px;
  padding-bottom: 4px;
}
.form-section-title {
  font-size: 13px;
  font-weight: 500;
  color: var(--primary);
  border-left: 3px solid var(--primary);
  padding-left: 8px;
  margin: 16px 0 14px;
}
.form-tip {
  margin-left: 12px;
  font-size: 12px;
  color: #909399;
  line-height: 1.4;
}

/* 弹框内整体样式（与全局弹框适配） */
.official-form-dialog >>> .el-dialog__body {
  padding: 14px 30px 4px;
}
.official-form-dialog >>> .wechat-editor {
  min-height: 320px;
}
</style>
